// User types
export interface User {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: UserRole;
  createdAt: Date;
  updatedAt: Date;
}

// Canonical roles (snake_case, lowercase)
export type CanonicalRole = 'super_admin' | 'municipality_admin' | 'municipality_user' | 'citizen';

// Canonical municipality roles (lowercase) - preferred for new code
export type CanonicalMunicipalityRole = 'admin' | 'support' | 'accountant' | 'engineer' | 'moderator';

// Backwards-compatible enum kept for existing imports; prefer CanonicalRole moving forward
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  MUNICIPALITY_ADMIN = 'municipality_admin',
  MUNICIPALITY_USER = 'municipality_user',
  CITIZEN = 'citizen',
}

/**
 * Municipality user roles for internal management.
 *
 * @deprecated Phase 7.3 – use string roleId from Firestore municipality_roles instead.
 * Kept for backward compatibility during migration. Map: ADMIN->admin, SUPPORT->support, etc.
 */
export enum MunicipalityUserRole {
  ADMIN = 'ADMIN',
  SUPPORT = 'SUPPORT',
  ACCOUNTANT = 'ACCOUNTANT',
  ENGINEER = 'ENGINEER',
  MODERATOR = 'MODERATOR',
}

// Municipality user interface
export interface MunicipalityUser extends User {
  role: UserRole.MUNICIPALITY_USER;
  municipalityId: string;
  /** Role ID from Firestore municipality_roles (e.g. "admin", "support"). Lowercase. */
  municipalityRole: string;
  name: string;
  surname: string;
  status: 'active' | 'inactive' | 'pending';
  lastLogin?: Date;
  invitedBy?: string;
  invitedAt?: Date;
  /** User-level permission overrides (Phase 5 – USER_ROLES.md). Municipality admin only. */
  municipalityRoleOverrides?: Record<string, { create?: boolean; read?: boolean; update?: boolean; delete?: boolean }>;
}

// Municipality types
export interface Municipality {
  id: string;
  name: string;
  code: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  logo?: string;
  createdAt: Date;
  updatedAt: Date;
}

// News types (shared between web and mobile apps)
export interface NewsArticle {
  id: string;
  municipalityId: string;
  translations: Record<string, NewsTranslation>;
  category: NewsCategory;
  status: NewsStatus;
  visibility: NewsVisibility;
  featuredImage?: string;
  attachments: string[];
  publishDate?: Date;
  isPinned: boolean;
  isFeatured: boolean;
  slug: string;
  tags: string[];
  viewCount: number;
  uniqueViewCount: number;
  averageReadTime: number;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  pushNotificationSent: boolean;
  pushNotificationSentAt?: Date;
}

export interface NewsTranslation {
  title: string;
  content: string;
  excerpt?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export enum NewsCategory {
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  EVENT = 'EVENT',
  NEWS = 'NEWS',
  ALERT = 'ALERT',
  UPDATE = 'UPDATE',
}

export enum NewsStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum NewsVisibility {
  PUBLIC = 'PUBLIC',
  AUTHENTICATED = 'AUTHENTICATED',
  PRIVATE = 'PRIVATE',
}

// Citizen-specific types
export interface CitizenUser extends User {
  role: UserRole.CITIZEN;
  name: string;
  surname: string;
  phoneNumber: string;
  address?: Address;
  notificationSettings?: NotificationSettings;
  preferences?: UserPreferences;
  selectedMunicipalities?: string[]; // Array of municipality IDs
}

export interface Address {
  address1: string;
  address2?: string;
  city: string;
  country: 'Cyprus' | 'Greece';
  postcode?: string;
}

export interface NotificationSettings {
  systemNotifications: boolean;
  municipalityNotifications: boolean;
  regionalAlerts: boolean;
  lostAndFoundAlerts: boolean;
  volunteeringNotifications: boolean;
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'auto';
  fontSize?: 'small' | 'medium' | 'large';
  language?: string;
  accessibility?: AccessibilitySettings;
}

export interface AccessibilitySettings {
  highContrast?: boolean;
  screenReader?: boolean;
  reducedMotion?: boolean;
}

// Service types
export interface Service {
  id: string;
  municipalityId: string;
  name: string;
  description: string;
  category: ServiceCategory;
  status: ServiceStatus;
  requirements: string[];
  documents: string[];
  createdAt: Date;
  updatedAt: Date;
}

export enum ServiceCategory {
  DOCUMENTS = 'DOCUMENTS',
  PERMITS = 'PERMITS',
  PAYMENTS = 'PAYMENTS',
  COMPLAINTS = 'COMPLAINTS',
  OTHER = 'OTHER',
}

export enum ServiceStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE',
}

// Request types
export interface ServiceRequest {
  id: string;
  serviceId: string;
  userId: string;
  status: RequestStatus;
  documents: string[];
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export enum RequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

// Notification types
export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: NotificationType;
  read: boolean;
  createdAt: Date;
}

export enum NotificationType {
  REQUEST_UPDATE = 'REQUEST_UPDATE',
  SYSTEM = 'SYSTEM',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
}

// Common types
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// ========================================
// ROLE HELPERS (Single Source of Truth)
// ========================================

/**
 * Normalize role string to canonical lowercase format
 * @param role - Role string in any format (uppercase, lowercase, mixed)
 * @returns Canonical role or null if invalid
 */
export const normalizeRole = (role?: string | null): CanonicalRole | null => {
  if (!role) return null;
  const r = role.toLowerCase();
  switch (r) {
    case 'super_admin':
    case 'super-admin':
    case 'superadmin':
      return 'super_admin';
    case 'municipality_admin':
    case 'municipality-admin':
    case 'municipalityadmin':
    case 'admin_municipality':
      return 'municipality_admin';
    case 'municipality_user':
    case 'municipality-user':
    case 'municipalityuser':
      return 'municipality_user';
    case 'citizen':
      return 'citizen';
    default:
      return null;
  }
};

/**
 * Normalize municipality role to canonical lowercase format
 * @param municipalityRole - Municipality role in any format
 * @returns Canonical municipality role or null if invalid
 */
export const normalizeMunicipalityRole = (municipalityRole?: string | null): CanonicalMunicipalityRole | null => {
  if (!municipalityRole) return null;
  const r = municipalityRole.toLowerCase();
  switch (r) {
    case 'admin':
      return 'admin';
    case 'support':
      return 'support';
    case 'accountant':
      return 'accountant';
    case 'engineer':
      return 'engineer';
    case 'moderator':
      return 'moderator';
    default:
      return null;
  }
};

/**
 * Parse role with fallback to citizen
 * Use this when you need a guaranteed role value
 */
export const parseRole = (role?: string | null): CanonicalRole => {
  return normalizeRole(role) || 'citizen';
};

/**
 * Parse municipality role with fallback to support
 * Use this when you need a guaranteed municipality role value
 */
export const parseMunicipalityRole = (municipalityRole?: string | null): CanonicalMunicipalityRole => {
  return normalizeMunicipalityRole(municipalityRole) || 'support';
};

// Role checker functions (case-insensitive)
export const isSuperAdmin = (role?: string | null): boolean => normalizeRole(role) === 'super_admin';
export const isMunicipalityAdmin = (role?: string | null): boolean => normalizeRole(role) === 'municipality_admin';
export const isMunicipalityUser = (role?: string | null): boolean => normalizeRole(role) === 'municipality_user';
export const isCitizen = (role?: string | null): boolean => normalizeRole(role) === 'citizen';

/**
 * Check if municipality role has elevated permissions (admin or moderator)
 * Case-insensitive comparison
 */
export const hasElevatedMunicipalityRole = (municipalityRole?: string | null): boolean => {
  const normalized = normalizeMunicipalityRole(municipalityRole);
  return normalized === 'admin' || normalized === 'moderator';
};

/**
 * Get human-readable label for role
 */
export const getRoleLabel = (role?: string | null): string => {
  const r = normalizeRole(role);
  switch (r) {
    case 'super_admin':
      return 'Super Administrator';
    case 'municipality_admin':
      return 'Municipality Administrator';
    case 'municipality_user':
      return 'Municipality User';
    case 'citizen':
      return 'Citizen';
    default:
      return role || 'Unknown';
  }
};

/**
 * Get human-readable label for municipality role
 */
export const getMunicipalityRoleLabel = (municipalityRole?: string | null): string => {
  const r = normalizeMunicipalityRole(municipalityRole);
  switch (r) {
    case 'admin':
      return 'Administrator';
    case 'support':
      return 'Support';
    case 'accountant':
      return 'Accountant';
    case 'engineer':
      return 'Engineer';
    case 'moderator':
      return 'Moderator';
    default:
      return municipalityRole || 'Unknown';
  }
};

/** Ant Design Tag color for municipality role (roleId lowercase) */
const MUNICIPALITY_ROLE_COLORS: Record<string, string> = {
  admin: 'red',
  moderator: 'blue',
  support: 'green',
  accountant: 'orange',
  engineer: 'purple',
};

/**
 * Get Ant Design Tag color for municipality role display.
 */
export const getMunicipalityRoleColor = (roleId?: string | null): string => {
  if (!roleId) return 'default';
  return MUNICIPALITY_ROLE_COLORS[String(roleId).toLowerCase()] ?? 'default';
};

/**
 * Check if a user has permission to perform admin actions
 * @param role - User's main role
 * @param municipalityRole - User's municipality-specific role (if municipality_user)
 */
export const hasAdminPermission = (role?: string | null, municipalityRole?: string | null): boolean => {
  return isSuperAdmin(role) || isMunicipalityAdmin(role) || hasElevatedMunicipalityRole(municipalityRole);
};

// ========================================
// CHAT TYPES
// ========================================

export type ConversationType = 'direct' | 'group';
export type MessageType = 'text' | 'emoji' | 'system';
export type UserStatus = 'online' | 'away' | 'offline';
export type ChatScope = 'municipality' | 'super_admin';

/** Encryption key version for chat message key rotation */
export type EncryptionKeyVersion = 'v1' | 'v2';

export interface Conversation {
  id: string;
  type: ConversationType;
  scope: ChatScope;
  municipalityId?: string;
  name?: string;
  description?: string;
  avatar?: string;
  participantIds: string[];
  participantDetails: Record<string, ParticipantInfo>;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  lastMessage?: LastMessage;
  unreadCount: Record<string, number>;
  typingUsers: string[];
  isArchived: Record<string, boolean>;
  isMuted: Record<string, boolean>;
  pinnedBy: string[];
  deletedBy: string[];
}

export interface ParticipantInfo {
  name: string;
  avatar?: string;
  role: string;
  addedAt: Date;
  addedBy: string;
}

export interface LastMessage {
  content: string;
  senderId: string;
  senderName: string;
  timestamp: Date;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  type: MessageType;
  metadata?: MessageMetadata;
  /** Which encryption key was used; default v1 for legacy messages */
  encryptionKeyVersion?: EncryptionKeyVersion;
  createdAt: Date;
  updatedAt?: Date;
  editedAt?: Date;
  isEdited: boolean;
  deletedBy: string[];
  readBy: Record<string, Date>;
  deliveredTo: Record<string, Date>;
  reactions: Record<string, string>;
  expiresAt: Date;
}

export interface MessageMetadata {
  emojis?: string[];
  mentions?: string[];
  replyTo?: string;
}

// ========================================
// CONTENT ATTRIBUTION
// ========================================

/**
 * Content attribution metadata for tracking who created/modified content
 * Used across all content modules (News, Events, Surveys, POIs, etc.)
 */
export interface ContentAttribution {
  authorId: string;
  authorName: string;
  authorRole: string;
  authorType: 'super_admin' | 'municipality_user';
}

/**
 * Build content attribution from the current user context
 * 
 * @param userId - The user's UID
 * @param displayName - The user's display name or email
 * @param role - The user's top-level role (e.g., 'super_admin', 'municipality_user')
 * @param specificRole - The user's specific role (superAdminRole or municipalityRole)
 * @returns ContentAttribution object ready to spread into Firestore documents
 */
export const buildContentAttribution = (
  userId: string,
  displayName: string,
  role: string,
  specificRole?: string,
): ContentAttribution => {
  const authorType = isSuperAdmin(role) ? 'super_admin' : 'municipality_user';
  
  let authorRole = 'User';
  if (isSuperAdmin(role) && specificRole) {
    // Use the super admin role label
    authorRole = specificRole;
  } else if (specificRole) {
    // Use the municipality role label
    authorRole = specificRole;
  } else if (isMunicipalityAdmin(role)) {
    authorRole = 'Admin';
  }

  return {
    authorId: userId,
    authorName: displayName,
    authorRole,
    authorType,
  };
};

/**
 * Format content attribution for display
 * Returns "Name (Role)" format
 */
export const formatAttribution = (
  authorName?: string,
  authorRole?: string,
): string => {
  if (!authorName) return 'Unknown';
  if (!authorRole) return authorName;
  return `${authorName} (${authorRole})`;
};

// Export volunteering types
export * from './volunteering';

export interface UserPresence {
  userId: string;
  status: UserStatus;
  lastSeen: Date;
  currentConversationId?: string;
  isTyping: boolean;
  scope: ChatScope;
  municipalityId?: string;
}

export interface ChatNotification {
  conversationId: string;
  messageId: string;
  senderId: string;
  senderName: string;
  preview: string;
  timestamp: Date;
  type: ConversationType;
}

// Lost and Found types
export * from './lostAndFound';

// Issue Reporting types
export * from './issueReporting';

// Knowledge base types
export * from './knowledgeBase';

// Shared language types and utilities
export * from './languages';

// Super Admin types and helpers
export * from './superAdmin';

// Dynamic municipality roles (USER_ROLES.md)
export * from './municipalityRoles';

// Push notification types
export * from './pushNotifications';